import { DataTable, DeleteDialog, Dialog, FileInput, FormLabel, FormSectionHeader, Heading, Loader, Select } from "@/components";
import {
  Input,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Pencil,
  Search,
} from "lucide-react";
import useProperties from "./useProperties";
import { useIsMobile } from "@/hooks/use-mobile";
import { tabs } from "./consts";
import { OwnershipHistory } from "./OwnershipHistory";

const Properties = () => {
  const {
    onAddProperties,
    globalFilter,
    setGlobalFilter,
    columns,
    breadcrumbData,
    isLoadingProperties,
    propertiesData,
    openDialog,
    onClose,
    onConfirm,
    isDeletePending,
    propertyDeleteState,
    isLoadingPropertyDeleteState,
    selectedRow,
    onRowClick,
    isLoadingBom,
    isTemplateDownloading,
    downloadTemplate,
    setShowBulkDialog,
    showBulkDialog,
    file,
    fileInputRef,
    setFile,
    onUpload,
    isBulkDownloading,
    onBulkDownload,
    communityList,
    communityId,
    onCommunityChange,
    isCommunityLoading,
    isMasterData,
    title,
    openOwnershipDialog,
    setOpenOwnershipDialog,
    onBulkUpload,
    hasAddPermission
  } = useProperties();
  const isMobile = useIsMobile();
  const bulkUploadTitle = `Bulk Upload Property`;

  return (
    <div className="p-4 md:p-6"> 
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-1">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={title}
            breadcrumbData={breadcrumbData as any}
          />
          {isMobile && hasAddPermission && (
            <Button onClick={onAddProperties} className="px-2" disabled={isCommunityLoading}>
              + Add Property
            </Button>
          )}
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mt-4 md:mt-auto">
          {isMasterData ? <div className="relative col-span-2 w-full sm:w-72 sm:min-w-72 sm:max-w-72 shrink-0">
            <FormLabel>Select Community</FormLabel>
            <Select
              placeholder={""}
              searchPlaceholder={""}
              noDataPlaceholder={"Community No Found"}
              options={communityList?.data?.map((data: any) => ({
                value: data?.communityId?.toString(),
                label: data?.name,
              }))}
              value={communityId?.toString()}
              onChange={onCommunityChange}
              className="h-[36px] w-full"
              disabled={isCommunityLoading}
            />
          </div> : <></>}
          <Input
            type="text"
            placeholder="Search"
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full sm:w-72 bg-white h-[40px]"
            startIcon={<Search className="w-4 h-4" />}
          />
          {!isMobile && hasAddPermission && (
            <Button onClick={onAddProperties}>+ Add Property</Button>
          )}
        </div>
      </div>

      {/* Responsive Table */}
      {isLoadingProperties || isLoadingBom ? (
        <Loader />
      ) : (
        <>
          {isMasterData ? <div className="my-5 flex flex-wrap items-center justify-end">
            <Button variant="ghost" className="text-gray-500" disabled={isBulkDownloading} onClick={onBulkDownload}><ArrowDownToLine /> Bulk Download</Button>
            {hasAddPermission ? 
              <>
                <Button variant="ghost" className="text-gray-500" disabled={isTemplateDownloading} onClick={downloadTemplate}><ArrowDownToLine /> Download Template</Button>
                <Button variant="ghost" className="text-gray-500" disabled={isTemplateDownloading || isBulkDownloading} onClick={onBulkUpload}><ArrowUpFromLine /> Bulk Upload</Button>
              </> : <></>}
          </div> : <div className="mb-5" />}

          <DataTable
            columns={columns}
            data={(propertiesData?.data || [])?.sort(
              (a: any, b: any) =>
                new Date(b.updatedAt).getTime() -
                new Date(a.updatedAt).getTime()
            )}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            onRowClick={onRowClick}
          />
          <DeleteDialog
            open={openDialog}
            onClose={onClose}
            onConfirm={onConfirm}
            isLoading={isDeletePending}
            optionalReason
            contents={[
              <div>
                {`Deleting `}
                <b className="text-primary">{selectedRow?.name}</b>
                {` property will permanently remove all related data. This action cannot be undone. Do you want to proceed?`}
              </div>,
              <div>
                <div>
                  {`Deleting `}
                  <b className="text-primary">{selectedRow?.name}</b>
                  {` property will permanently remove all related data. This action cannot be undone. Do you want to proceed? Below items are affected:`}
                </div>

                {isLoadingPropertyDeleteState ? (
                  <Loader />
                ) : (
                  <div className="flex flex-col gap-1 mt-2">
                    <span>
                      <i className="text-primary">Number of Users : </i>
                      {propertyDeleteState?.data?.ownershipCount || 0}
                    </span>
                    <span>
                      <i className="text-primary">
                        Number of financial Records :{" "}
                      </i>
                      {propertyDeleteState?.data?.financialRecordCount || 0}
                    </span>
                  </div>
                )}
              </div>,
            ]}
          />
        </>
      )}
      <Dialog open={showBulkDialog} setOpen={setShowBulkDialog} title={bulkUploadTitle} onSave={onUpload}>
        <div className="relative">
          <label className="text-[14px]">File Upload <span className="text-[12px] text-gray-400">(Max limit 10,000)</span></label>
          <FileInput
            file={file}
            fileInputRef={fileInputRef}
            setFile={setFile}
            accept=".xlsx, .excel"
          />
        </div>
      </Dialog>

      <Dialog
        open={openOwnershipDialog}
        setOpen={setOpenOwnershipDialog}
        title={
          <div className="text-2xl font-bold text-primary">
            {"View Ownership History"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
        cancelText="Close"
        contentClassName="!max-w-[600px] !w-[600px]"
      >
          <div>
            <FormSectionHeader className="font-semibold">{`${selectedRow?.propertyId} - ${selectedRow?.name}`}</FormSectionHeader>

            <Tabs defaultValue="overview" className="w-full my-4">
              <TabsList className="gap-x-4 bg-white">
                {tabs?.map((data: any) => (
                  <TabsTrigger
                    key={data?.value}
                    value={data?.value}
                    className="bg-transparent border-0 shadow-none hover:cursor-pointer text-gray-400 px-0 border-0 shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-black"
                  >
                    {data?.title}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview" className="w-full">
                <>
                  <div className="bg-blue-50 p-3 flex flex-row items-center justify-between rounded-md">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="text-white bg-primary rounded-full h-10 w-10 flex items-center justify-center">RB</div>
                      <div className="flex flex-col">
                        <span>Current Owner</span>
                        <span className="font-bold">{selectedRow?.occupancyUserName || ""}</span>
                        <span>Ownership ID: {selectedRow?.occupancyUserId || ""}</span>
                      </div>
                    </div>
                    <Button>
                      <Pencil /> Change Ownership
                    </Button>
                  </div>

                  <div className="mt-5 rounded-md border-1 border-gray-200 bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                      <div className="flex flex-col">
                        <span className="text-[14px] font-normal">Residency Type</span>
                        <span className="text-[16px] font-semibold">Owner</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[14px] font-normal">Unit No.</span>
                        <span className="text-[16px] font-semibold">{selectedRow?.unitNumber}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 mt-3 pt-3 border-t-1 border-gray-200">
                      <div className="flex flex-col">
                        <span className="text-[14px] font-normal">Zone</span>
                        <span className="text-[16px] font-semibold">{selectedRow?.zone}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[14px] font-normal">Block</span>
                        <span className="text-[16px] font-semibold">{selectedRow?.block}</span>
                      </div>
                    </div>
                  </div>
                </>
              </TabsContent>
              <TabsContent value="history" className="w-full">
                <OwnershipHistory />
              </TabsContent>
            </Tabs>
          </div>
      </Dialog>
    </div>
  );
};

export default Properties;
